import { z } from 'zod';

export * from './entry-type';
export * from './create';

/**
 * The status of a schema determines whether it is available for use in the workspace.
 * Only schemas with an `active` status are available for use.
 */
export const schemaStatuses = ['draft', 'active', 'archived'] as const;

export type SchemaStatus = (typeof schemaStatuses)[number];

/**
 * Represents a serialized schema stored in the database, alongside metadata.
 */
export type SchemaListItem = {
  id: string;
  version: number;
  domain: string;
  type: string;
  variant: SchemaVariant;
  name: string;
  description: string;
  operatorId: string[];
  updateTemplateDiscardFields: string[];
  config?: Record<string, unknown>;
  definition: Record<string, unknown>;
  status: SchemaStatus;
  createdBy: string;
  createdAt: string;
  updatedBy: string;
  updatedAt: string;
};

export type CreateSchemaInput = {
  domain: string;
  type: string;
  variant: string;
  name: string;
  description: string;
  operatorId: string[];
  updateTemplateDiscardFields: string[];
  config?: Record<string, unknown>;
  definition: Record<string, unknown>;
  createdBy?: string;
};

export type UpdateSchemaInput = Partial<
  Omit<CreateSchemaInput, 'createdBy'>
> & {
  updatedBy?: string;
};

/**
 * The registry models the set of allowed values that distinguish/categorize schemas from each other.
 * These values impact runtime behavior, and also allow schema selection during the setup phase.
 */
export type SchemaRegistry = {
  schemaDomains: SchemaDomain[];
  schemaTypes: SchemaType[];
  schemaVariants: SchemaVariant[];
  schemaRules: Partial<
    Record<
      SchemaDomain,
      {
        allowedTypes?: SchemaType[];
        allowedVariants?: SchemaVariant[];
      }
    >
  >;
};

/**
 * The schema variant impacts the chosen implementation of the node logic blocks. It also determines
 * which file view to use for template editing in the workspace.
 */
export enum SchemaDomain {
  Intermodal = 'intermodal',
  Retail = 'retail',
  EasyEom = 'easyeom',
  Update = 'update',
}

export enum SchemaType {
  Import = 'import',
  Export = 'export',
  Standard = 'standard',
  Invoice = 'invoice',
}

export enum SchemaVariant {
  Pdf = 'pdf',
  Table = 'xls/csv',
  Xml = 'xml',
  Text = 'text',
  Email = 'email',
  Json = 'json',
}

export enum SchemaSource {
  File = 'file',
  Email = 'email',
  Http = 'http',
}

export enum EdiType {
  POST = 'post',
  GET = 'get',
  TokenBased = 'token',
}

export type EdiInput = {
  id: string;
  label: string;
  type: string;
  schemaVariable: string;
  internal?: boolean;
};

// Credential fields rendered per EDI connection method. schemaVariable is the
// runtime variable name the processor injects the value under (e.g. $ediUser).
export const ediOptionInputs: Record<EdiType, EdiInput[]> = {
  post: [
    { id: 'user', label: 'User', type: 'text', schemaVariable: '$ediUser' },
    {
      id: 'password',
      label: 'Password',
      type: 'password',
      schemaVariable: '$ediPassword',
    },
  ],
  get: [
    { id: 'user', label: 'User', type: 'text', schemaVariable: '$ediUser' },
    {
      id: 'password',
      label: 'Password',
      type: 'password',
      schemaVariable: '$ediPassword',
    },
    {
      id: 'oauth',
      label: 'OAUTH',
      type: 'text',
      schemaVariable: '$ediOAuth',
      internal: true,
    },
  ],
  token: [
    { id: 'user', label: 'User', type: 'text', schemaVariable: '$ediUser' },
    {
      id: 'password',
      label: 'Password',
      type: 'text',
      schemaVariable: '$ediPassword',
    },
    {
      id: 'endpoint',
      label: 'Endpoint',
      type: 'text',
      schemaVariable: '$ediEndpoint',
    },
  ],
};

// Used to identify EDI credential variables when scanning a schema's variable map.
export const ediVariableNames = new Set(['ediUser', 'ediPassword', 'ediOAuth']);

export enum ReservedNodeType {
  Data = '$data',
  DeclareVariable = '$declareVariable',
  Iterator = '#iterator',
  RepeatItems = '#repeatItems',
  ParseInt = '#parseInt',
}

// Identifiers pre-injected into the schema runtime scope. Schema node keys must
// not collide with these names or the processor will silently shadow them.
// TODO: check if this breaks: ##>4$get
export const reservedNames = new Set([
  'Array.classify',
  'Array.forEach',
  'Array.map',
  'CONTAINERNR',
  'FILEID',
  'RegExp',
  'db',
  'data',
  'declareVariable',
  'emailData',
  'emailMessage',
  'emailMessage.from.0.address',
  'emailMessage.text',
  'emailMessage.subject',
  'emailData.fileName',
  'pdfString',
  'txtstring',
  'flag',
  'genericOrigin',
  'getVariable',
  'keyGen',
  'mongoRequest',
  'options',
  'pdfString',
  'txtstring',
  'result',
  'skip',
  'time',
  'utils',
  'exists',
  'xls',
  'math',
  'helpers',
  'inputdocument',
  'global.utils',
  'ITERATOR',
  'CURRENT_COUNTER',
  'setVariable',
]);

export const reservedWrapVariables = new Map<string, [object, string]>([
  ['$xlsColumn', [{ '$data.val': [] }, '$genericXls.findVal']],
]);

// The _def fallback handles cross-realm instanceof failures (e.g. multiple Zod
// instances loaded in the same process, which breaks the prototype chain check).
export const isZodEnum = (schema: any): schema is z.ZodEnum<any> => {
  if (!schema) return false;
  if (schema instanceof z.ZodEnum) return true;
  if (schema._def?.typeName === 'ZodEnum') return true;

  return false;
};

export const isZodNumber = (schema: any): schema is z.ZodNumber => {
  if (!schema) return false;
  if (schema instanceof z.ZodNumber) return true;
  if (schema._def?.typeName === 'ZodNumber') return true;

  return false;
};
